/**
 * Token management utilities for automatic token refresh
 */

import { logger } from './logger';

// Cognito Configuration from Environment Variables
const COGNITO_CONFIG = {
  cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN || '',
  userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
};

interface TokenRefreshResponse {
  access_token: string;
  id_token: string;
  expires_in: number;
  token_type: string;
}

interface TokenInfo {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_at: number;
}

export class TokenManager {
  private static instance: TokenManager;
  private refreshPromise: Promise<TokenInfo> | null = null;

  private constructor() {}

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Check if tokens are close to expiration and need refresh
   */
  public shouldRefreshTokens(): boolean {
    const tokenExpiresAt = localStorage.getItem('token_expires_at');
    if (!tokenExpiresAt) return false;

    const expiresAt = parseInt(tokenExpiresAt);
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    
    // Refresh if token expires within the next 5 minutes
    return timeUntilExpiry <= 5 * 60 * 1000;
  }

  /**
   * Check if tokens are expired
   */
  public areTokensExpired(): boolean {
    const tokenExpiresAt = localStorage.getItem('token_expires_at');
    if (!tokenExpiresAt) return true;

    const expiresAt = parseInt(tokenExpiresAt);
    const now = Date.now();
    
    return now >= expiresAt;
  }

  /**
   * Refresh tokens using the refresh token
   */
  public async refreshTokens(): Promise<TokenInfo | null> {
    // If already refreshing, return the existing promise
    if (this.refreshPromise) {
      console.log('Token refresh already in progress, waiting for existing promise');
      return this.refreshPromise;
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      logger.error('No refresh token available');
      console.error('No refresh token available for refresh');
      return null;
    }

    console.log('Starting token refresh...');
    try {
      this.refreshPromise = this.performTokenRefresh(refreshToken);
      const result = await this.refreshPromise;
      this.refreshPromise = null;
      console.log('Token refresh completed successfully');
      return result;
    } catch (error) {
      this.refreshPromise = null;
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Perform the actual token refresh request
   */
  private async performTokenRefresh(refreshToken: string): Promise<TokenInfo> {
    const tokenEndpoint = `https://${COGNITO_CONFIG.cognitoDomain}/oauth2/token`;
    
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: COGNITO_CONFIG.userPoolClientId,
      refresh_token: refreshToken
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Token refresh failed:', { status: response.status, error: errorText });
      throw new Error(`Token refresh failed: ${response.status} ${errorText}`);
    }

    const tokenResponse: TokenRefreshResponse = await response.json();
    
    // Update stored tokens
    const expiresAt = Date.now() + tokenResponse.expires_in * 1000;
    
    localStorage.setItem('access_token', tokenResponse.access_token);
    localStorage.setItem('id_token', tokenResponse.id_token);
    localStorage.setItem('token_expires_at', expiresAt.toString());

    logger.log('Tokens refreshed successfully');

    return {
      access_token: tokenResponse.access_token,
      id_token: tokenResponse.id_token,
      refresh_token: refreshToken, // Keep the same refresh token
      expires_at: expiresAt
    };
  }

  /**
   * Clear all stored tokens
   */
  public clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user_info');
  }

  /**
   * Get current token info
   */
  public getTokenInfo(): TokenInfo | null {
    const accessToken = localStorage.getItem('access_token');
    const idToken = localStorage.getItem('id_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const expiresAt = localStorage.getItem('token_expires_at');

    if (!accessToken || !idToken || !refreshToken || !expiresAt) {
      return null;
    }

    return {
      access_token: accessToken,
      id_token: idToken,
      refresh_token: refreshToken,
      expires_at: parseInt(expiresAt)
    };
  }
}

// Export singleton instance
export const tokenManager = TokenManager.getInstance();
export default TokenManager;
